import { Trans, t } from "@lingui/macro";
import { ArrowLeft, MoreVertical, RefreshCw, Trash2, FileText } from "lucide-react";
import { useEffect, useState } from "react";
import { ChunkText, SoribotContentResponse } from "../../models/DTO";
import { Soribot } from "../../models/Soribot";
import { DataSourceType } from "../../models/SoribotSource";
import { Fire } from "../../services/FireClient";
import { DataSourceIcon } from "./compontents/Icons";
import { useHistory } from "../../hooks/useRouterCompat";
import { Button } from "../../components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../../components/ui/accordion";
import { Textarea } from "../../components/ui/textarea";
import { Popover, PopoverContent, PopoverTrigger } from "../../components/ui/popover";
import { Progress } from "../../components/ui/progress";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../components/ui/alert-dialog";

export const SoribotKnowledgeViewer: React.FC<{ bot: Soribot }> = ({ bot }) => {
  const [resp, setResp] = useState<SoribotContentResponse>();
  const [inProgress, setInProgress] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [popoverOpen, setPopoverOpen] = useState(false);
  const history = useHistory();

  useEffect(() => {
    reload();
  }, [bot.id]);

  function reload() {
    setInProgress(true);
    setResp(undefined);
    Fire.bot.getSoribotContent({ soribotId: bot.id })
      .then(setResp)
      .finally(() => setInProgress(false));
  }

  const chunkMap = getChunkMap();

  return <div className="space-y-4 p-4 max-w-4xl mx-auto">
    {inProgress && <Progress value={undefined} className="h-1 mb-4" />}

    {bot.subType === 'general' && <div className="p-3 bg-yellow-50 text-yellow-700 rounded text-sm border border-yellow-200 mb-4"><Trans>This page only applies to bots with Knowledge Retrieval.</Trans></div>}

    {!inProgress && resp && <>
      <div className="flex items-center justify-between gap-4 mb-4">
        <Button variant="ghost" onClick={() => history.goBack()} className="pl-0"><ArrowLeft className="w-4 h-4 mr-2" /> <Trans>Back</Trans></Button>
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="icon" onClick={reload}><RefreshCw className="w-5 h-5" /></Button>
          <Popover open={popoverOpen} onOpenChange={setPopoverOpen}>
            <PopoverTrigger asChild>
              <Button variant="ghost" size="icon"><MoreVertical className="w-5 h-5" /></Button>
            </PopoverTrigger>
            <PopoverContent className="w-56 p-0" align="end">
              <div className="flex flex-col">
                <Button variant="ghost" className="justify-start text-red-500 hover:text-red-700 hover:bg-red-50" onClick={deleteContentConfirm}>
                  <Trash2 className="w-4 h-4 mr-2" /> <Trans>Delete all knowledge</Trans>
                </Button>
              </div>
            </PopoverContent>
          </Popover>
        </div>
      </div>

      <div className="mb-6">
        <h4 className="text-xl font-bold truncate"><Trans>{bot.name}'s knowledge content</Trans></h4>
      </div>

      {resp.error && <div className="p-4 bg-gray-100 rounded text-sm mb-4">{resp.error.includes('does not exist.') ? t`No content found` : resp.error}</div>}

      {resp.chunks.length > 0 && <div className="mb-4 text-sm font-medium text-gray-500"><Trans>{chunkMap.length} documents with total {resp.chunks.length} chunks</Trans></div>}

      <Accordion type="multiple" className="space-y-2">
        {chunkMap.map(([title, list]) => {
          if (list.length > 0) {
            const type = list[0].meta.type as DataSourceType;
            return <AccordionItem key={title} value={title} className="border rounded bg-white">
              <AccordionTrigger className="px-4 py-3 hover:no-underline">
                <div className="flex items-center gap-3 w-full overflow-hidden text-left">
                  <span className="shrink-0">{DataSourceIcon(type)}</span>
                  <span className="truncate font-medium flex-1">{title}</span>
                  <span className="text-gray-400 text-xs font-normal shrink-0 mr-2"><Trans>{list.length} parts</Trans></span>
                </div>
              </AccordionTrigger>
              <AccordionContent className="p-0 border-t bg-gray-50/50">
                <div className="divide-y">
                  {list.map((item, i) => (
                    <div key={i} className="bg-white">
                      <div className="px-4 py-2 bg-gray-50 flex items-center justify-between text-xs text-gray-500 border-b border-gray-100">
                        <div className="truncate flex-1 font-mono pr-4">{item.meta.nr}: {item.chunk.text.substring(0, 50)}...</div>
                        <div className="shrink-0"><Trans>{item.chunk.text.length} chars</Trans></div>
                      </div>
                      <div className="p-4 space-y-4">
                        {item.meta.fullContent && <div className="text-xs font-bold text-gray-500 uppercase"><Trans>Searchable content</Trans></div>}
                        <Textarea value={item.chunk.text} readOnly className="min-h-[100px] text-sm font-mono bg-gray-50" />

                        {item.meta.fullContent && <div className="space-y-2">
                          <div className="text-xs font-bold text-gray-500 uppercase"><Trans>Full content</Trans></div>
                          <Textarea value={item.meta.fullContent} readOnly className="min-h-[150px] text-sm font-mono bg-gray-50" />
                        </div>}

                        {item.meta.pageId && <div className="text-sm">
                          <a href={`https://officenote.co.kr/page/${item.meta.pageId}`} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline flex items-center gap-1">
                            <FileText className="w-4 h-4" /> <Trans>Source Page</Trans>
                          </a>
                        </div>}
                      </div>
                    </div>
                  ))}
                </div>
              </AccordionContent>
            </AccordionItem>
          } else {
            return null;
          }
        })}
      </Accordion>
    </>}

    <AlertDialog open={deleteDialog} onOpenChange={setDeleteDialog}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle><Trans>Delete all knowledge</Trans></AlertDialogTitle>
          <AlertDialogDescription><Trans>Are you sure you want to delete all knowledge content on {bot.name}?</Trans></AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel><Trans>Cancel</Trans></AlertDialogCancel>
          <AlertDialogAction onClick={executeDelete} className="bg-red-500 hover:bg-red-600"><Trans>Delete</Trans></AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  </div>

  function getChunkMap() {
    const map = new Map<string, { meta: any, chunk: ChunkText }[]>();
    resp?.chunks.forEach(c => {
      const title = c.meta.title ?? t`Unknown page`;
      if (!map.has(title)) {
        map.set(title, [])
      }
      map.get(title)!.push({ meta: c.meta, chunk: c });
    });
    return Array.from(map);
  }

  function deleteContentConfirm() {
    setPopoverOpen(false);
    setDeleteDialog(true);
  }

  function executeDelete() {
    setInProgress(true);
    Fire.bot.deleteSoribotContent({ soribotId: bot.id }).then(reload);
    setDeleteDialog(false);
  }
}

export default SoribotKnowledgeViewer;